import React from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { TaxExpense } from '../../../types/database';
import { getExpenseCategory } from '../../../lib/expense-categories';
import { formatCurrency, formatMonth } from '../../../lib/format';
import { cn } from '../../../lib/utils';
import { calendarYearForMonth } from '../../../utils/taxUtils';
import { Button } from '../../ui/button';
import { ConfirmButton } from '../../ui/confirm-button';

interface ExpenseListProps {
  taxYearStart: number;
  expenses: TaxExpense[];
  /** Group under month headings with subtotals (used when sorting by month). */
  groupByMonth: boolean;
  editingId: number | null;
  onEdit: (expense: TaxExpense) => void;
  onDelete: (expense: TaxExpense) => void;
}

const ExpenseRow: React.FC<{
  expense: TaxExpense;
  monthLabel?: string;
  isEditing: boolean;
  onEdit: () => void;
  onDelete: () => void;
}> = ({ expense, monthLabel, isEditing, onEdit, onDelete }) => {
  const category = getExpenseCategory(expense.category);
  return (
    <li
      className={cn(
        'group flex items-center gap-3 rounded-lg px-2.5 py-2 transition-colors hover:bg-white/[0.03]',
        isEditing && 'bg-brand/[0.08] hover:bg-brand/[0.08]'
      )}
    >
      <span className="size-2 shrink-0 rounded-full" style={{ backgroundColor: category.color }} aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] text-fg">{expense.name}</p>
        <p className="truncate text-xs text-fg-subtle">
          {category.label}
          {monthLabel && <> · {monthLabel}</>}
        </p>
      </div>
      <span className="numeric text-[13px] text-fg">{formatCurrency(expense.amount)}</span>
      {/* Actions stay visible on touch screens, reveal on hover with a mouse */}
      <div className="flex items-center gap-0.5 transition-opacity [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-focus-within:opacity-100 [@media(hover:hover)]:group-hover:opacity-100">
        <Button variant="ghost" size="icon-sm" className="size-7" onClick={onEdit} aria-label={`Edit ${expense.name}`}>
          <Pencil className="size-3.5" />
        </Button>
        <ConfirmButton
          variant="ghost"
          size="icon-sm"
          className="size-7 border-0 bg-transparent text-fg-muted hover:bg-danger/10 hover:text-danger"
          onConfirm={onDelete}
          confirmLabel={
            <>
              <Trash2 className="size-3.5" />
              <span className="text-xs font-medium">Delete?</span>
            </>
          }
          armedClassName="h-7 w-auto gap-1.5 bg-danger/15 px-2.5 text-danger ring-1 ring-danger/40 ring-inset hover:bg-danger/25 animate-pop-in"
          aria-label={`Delete ${expense.name} (click twice)`}
        >
          <Trash2 className="size-3.5" />
        </ConfirmButton>
      </div>
    </li>
  );
};

export const ExpenseList: React.FC<ExpenseListProps> = ({ taxYearStart, expenses, groupByMonth, editingId, onEdit, onDelete }) => {
  const monthLabel = (month: number) => formatMonth(calendarYearForMonth(taxYearStart, month), month);

  if (!groupByMonth) {
    return (
      <ul className="flex flex-col">
        {expenses.map((e) => (
          <ExpenseRow
            key={e.id}
            expense={e}
            monthLabel={monthLabel(e.month)}
            isEditing={editingId === e.id}
            onEdit={() => onEdit(e)}
            onDelete={() => onDelete(e)}
          />
        ))}
      </ul>
    );
  }

  // Expenses arrive already sorted by month; split into consecutive month groups.
  const groups: { month: number; items: TaxExpense[] }[] = [];
  for (const e of expenses) {
    const last = groups[groups.length - 1];
    if (last && last.month === e.month) last.items.push(e);
    else groups.push({ month: e.month, items: [e] });
  }

  return (
    <div className="flex flex-col gap-3">
      {groups.map((group) => (
        <section key={group.month}>
          <header className="sticky top-[7.25rem] z-[1] md:top-16 flex items-baseline justify-between border-b border-line bg-surface/95 px-2.5 py-1.5 backdrop-blur-sm">
            <h4 className="text-xs font-medium tracking-wide text-fg-muted uppercase">{monthLabel(group.month)}</h4>
            <span className="numeric text-xs text-fg-subtle">
              {group.items.length} · <span className="text-fg-muted">{formatCurrency(group.items.reduce((s, e) => s + e.amount, 0))}</span>
            </span>
          </header>
          <ul className="mt-1 flex flex-col">
            {group.items.map((e) => (
              <ExpenseRow
                key={e.id}
                expense={e}
                isEditing={editingId === e.id}
                onEdit={() => onEdit(e)}
                onDelete={() => onDelete(e)}
              />
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
};
